import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import {
  Button,
  Divider,
  Dropdown,
  Form,
  Input,
  Menu,
  Modal,
  Space,
  Spin,
  Tooltip,
  message,
} from 'antd';
import { PageProps } from '@/types/PageProps';
import AppPageHeader from '@/components/_common/AppPageHeader';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import InfiniteTable from '@/components/_common/InfiniteTable';
import { getTableScrollX } from '@/utils/table';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { ColumnsType } from 'antd/lib/table';
import { RefreshCcw } from 'react-feather';
import {
  useEmployeeCommissionCrete,
  useEmployeeCommissionList,
  useEmployeeCommissionRemove,
} from '@/core/domain/enterprise/EmployeeCommission/EmployeeCommission.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import {
  EmployeeCommission,
  EmployeeCommissionListFormValues,
  EmployeeCommissionListRequest,
  EmployeeCommissionSituations,
} from '@/core/domain/enterprise/EmployeeCommission/EmployeeCommission.types';
import moment from 'moment';
import { Check } from 'react-feather';
import { useDebounce, useDisclosure } from '@/hooks';
import EmployeeCommissionsFilter from '@/components/Movements/EmployeeCommissions/EmployeeCommissionsFilter';
import EmployeeCommissionReportModal from '@/components/Movements/EmployeeCommissions/EmployeeCommissionReportModal';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import formatterPrice from '@/utils/number/formatterPrice';
import EmployeePayCommissionsModal from '@/components/Movements/EmployeeCommissions/EmployeePayCommissionsModal';
import { InputDate } from '@/components/_common';
import EmployeePayCommissionModal from '@/components/Movements/EmployeeCommissions/EmployeePayCommissionModal';
import { DateTimeFormat } from '@/utils/moment';

type Item = EmployeeCommission & { checked: boolean; disabled: boolean };

const EmployeeCommissionsPage: React.FC<PageProps> = ({ route }: PageProps) => {
  const intl = useIntl();
  const [items, setItems] = useState<Item[]>([]);
  const [modalReportOpen, modalReportAction] = useDisclosure();

  const [form] = Form.useForm<EmployeeCommissionListFormValues>();
  const formValues = Form.useWatch([], form);

  const [modalComissionsOpen, modalComissionsAction] = useDisclosure();
  const [modalComissionOpen, modalComissionAction] = useDisclosure();
  const [selectedCommission, setSelectedCommission] =
    useState<EmployeeCommission>();

  const [queryParams, setQueryParams] = useState<EmployeeCommissionListRequest>(
    {
      order: 'DESC',
      ...convertParams<EmployeeCommissionListRequest>({
        order: 'string',
        codeWorkOrder: 'number',
        customerName: 'string',
        employeeIds: 'array-number',
        employeeName: 'string',
        from: 'string',
        licensePlate: 'string',
        offset: 'number',
        situation: 'string',
        to: 'string',
      }),
    },
  );

  const params = useDebounce(queryParams);
  usePersistSearchParams(params);

  const {
    data: employeeCommissions,
    dataUpdatedAt,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useEmployeeCommissionList(
    (!!params?.from && !!params?.to) || (!params?.from && !params?.to)
      ? params
      : { ...params, from: undefined, to: undefined },
  );
  const comissions = flatInfiniteData(employeeCommissions);

  const employeeComissionCreate = useEmployeeCommissionCrete();
  const employeeComissionRemove = useEmployeeCommissionRemove();

  useEffect(() => {
    if (comissions.length) {
      setItems(
        comissions.map((comission) => ({
          ...comission,
          checked: !!comission.payDate,
          disabled: !!comission.payDate,
        })),
      );
    }
  }, [dataUpdatedAt]);

  const getIconClass = (item: EmployeeCommission) =>
    `cursor-pointer ${!hasChecked(item) ? 'icon-disabled' : ''}`;

  const hasChecked = (item: EmployeeCommission) =>
    items.some((i) => getKey(i) === getKey(item) && i.checked);

  const itemsCheckedKeys = () =>
    items.filter((item) => item.checked).map((item) => getKey(item));

  const getItem = (item: EmployeeCommission) =>
    items.find((i) => getKey(i) === getKey(item));

  const handleChange = (
    [key, value]: [keyof EmployeeCommission, unknown],
    id: string,
  ) => {
    setItems((state) =>
      state.map((item) =>
        getKey(item) === id ? { ...item, [key]: value } : item,
      ),
    );
  };

  const getKey = (item: {
    workOrderItem: number;
    employeeId: number;
    employeeCommissionId?: number;
  }) =>
    `${item.workOrderItem}/${item.employeeId}/${Number(
      item.employeeCommissionId,
    )}`;

  async function verifyAndSubmit(itemComission: EmployeeCommission) {
    const itemFinded = items.find(
      (item) => getKey(item) === getKey(itemComission),
    );

    if (!itemFinded || !itemFinded.payDate) {
      return message.warning('Informe a data de pagamento');
    }

    submitCommissions();
  }

  async function submitCommission() {
    if (!selectedCommission) return;

    if (!items?.length) {
      return message.error('Informe uma comissão para ser registrada!');
    }

    await employeeComissionCreate.mutateAsync({
      isGenerateAccountsPayable: formValues?.isGenerateAccountsPayable,
      isGroupPayableByEmployee: formValues?.isGroupPayableByEmployee,
      employeeCommissions: [
        {
          companyId: selectedCommission?.companyId,
          employeeId: selectedCommission?.employeeId,
          observation:
            formValues?.comissionObservation || selectedCommission?.observation,
          payDate: formValues?.comissionDate
            ? moment(formValues.comissionDate).format(DateTimeFormat)
            : (selectedCommission?.payDate as string),

          workOrderItem: selectedCommission?.workOrderItem,
          value: selectedCommission?.commissionValue,
          planAccountId: formValues?.planAccountId,
          documentTypeId: formValues?.documentTypeId,
        },
      ],
    });
  }

  async function submitCommissions() {
    if (!items?.length) {
      return message.error(
        'Informe uma ou mais comissões para ser registradas!',
      );
    }

    await employeeComissionCreate.mutateAsync({
      isGenerateAccountsPayable: formValues?.isGenerateAccountsPayable,
      isGroupPayableByEmployee: formValues?.isGroupPayableByEmployee,
      employeeCommissions: items
        .filter((item) => item.checked && !item.employeeCommissionId)
        .map((item) => ({
          companyId: item.companyId,
          employeeId: item.employeeId,
          observation: formValues?.comissionObservation || item.observation,
          payDate: formValues?.comissionDate || (item.payDate as string),
          workOrderItem: item.workOrderItem,
          value: item.commissionValue,
          planAccountId: formValues?.planAccountId,
          documentTypeId: formValues?.documentTypeId,
        })),
    });
  }

  function confirmRemove(row: EmployeeCommission) {
    Modal.confirm({
      title: 'Estornar comissão',
      content: `Deseja estornar o registro da comissão quitada do funcionário "${row.nameEmployee}"?`,
      okText: 'Estornar',
      onOk: () => employeeComissionRemove.mutateAsync(row.employeeCommissionId),
    });
  }

  function saveLoading(row: EmployeeCommission) {
    return (
      employeeComissionCreate.variables?.employeeCommissions.some(
        (item) => getKey(item) === getKey(row),
      ) && employeeComissionCreate.isLoading
    );
  }

  const handlerPayGenerateAccountPayable = (row: EmployeeCommission) => {
    const comissionDate = getItem(row)?.payDate
      ? moment(getItem(row)?.payDate)
      : null;

    form.setFieldValue('comissionDate', comissionDate);
    setSelectedCommission(row);
    modalComissionAction.open();
  };

  const columns: ColumnsType<EmployeeCommission> = [
    {
      dataIndex: 'nameEmployee',
      title: 'Funcionário',
      width: 120,
      ellipsis: true,
    },
    {
      dataIndex: 'codeVersionWorkOrder',
      title: 'OS',
      width: 90,
      ellipsis: true,
      render: (value) => value.replace(/\s/g, '') || '-',
    },
    {
      dataIndex: 'licensePlate',
      title: 'Placa',
      width: 90,
    },
    {
      dataIndex: 'companyName',
      title: 'Empresa',
      width: 90,
    },
    {
      dataIndex: 'nameCustomer',
      ellipsis: true,
      title: 'Cliente',
      width: 90,
    },
    {
      dataIndex: 'situation',
      title: 'Sit. OS',
      width: 90,
      render: (_, row) => EmployeeCommissionSituations[row.situation] || '-',
    },
    {
      dataIndex: 'authorizationDate',
      title: 'Autorização',
      width: 90,
      render: (date) => (date ? moment(date).format('DD/MM/YYYY') : '-'),
    },
    {
      dataIndex: 'closingDate',
      title: 'Fechamento',
      width: 90,
      render: (date) => (date ? moment(date).format('DD/MM/YYYY') : '-'),
    },
    {
      dataIndex: 'liquidValue',
      title: 'Valor liq(R$)',
      width: 90,
      align: 'right',
      render: (value) => (value ? formatterPrice(value) : '-'),
    },
    {
      dataIndex: 'baseValue',
      title: 'Valor Base(R$)',
      width: 90,
      align: 'right',
      render: (value) => (value ? formatterPrice(value) : '-'),
    },
    {
      dataIndex: 'commissionValue',
      title: 'Comissão(R$)',
      width: 95,
      align: 'right',
      render: (value) => (value ? formatterPrice(value) : '-'),
    },
    {
      dataIndex: 'payDate',
      title: 'Dt. pagamento',
      width: 120,
      render: (_, row) => (
        <InputDate
          value={getItem(row)?.payDate ? moment(getItem(row)?.payDate) : null}
          onChange={(value) =>
            handleChange(['payDate', value?.format('YYYY-MM-DD')], getKey(row))
          }
          disabled={!hasChecked(row) || getItem(row)?.disabled}
        />
      ),
    },
    {
      dataIndex: 'observation',
      title: 'Observação',
      width: 120,
      render: (_, row) => (
        <Input
          value={getItem(row)?.observation}
          onChange={({ target }) =>
            handleChange(['observation', target.value], getKey(row))
          }
          disabled={!hasChecked(row) || getItem(row)?.disabled}
        />
      ),
    },
    {
      title: 'Ação',
      width: 60,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <Space>
          {!!row.payDate &&
            (row.accountPayableId ? (
              <Tooltip
                title={`Conta a Pagar: ${
                  row.codeAccountPayable || 'Indisponível'
                }`}
              >
                <RefreshCcw style={{ color: 'gray' }} size={18} />
              </Tooltip>
            ) : (
              <Tooltip title="Estornar">
                <RefreshCcw
                  className="cursor-pointer"
                  size={18}
                  onClick={() => confirmRemove(row)}
                />
              </Tooltip>
            ))}

          {!row.payDate && !saveLoading(row) && (
            <Tooltip title="Quitar" popupVisible={hasChecked(row)}>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="justQuit"
                      onClick={() => verifyAndSubmit(row)}
                    >
                      Apenas Quitar
                    </Menu.Item>

                    <Menu.Item
                      key="payGenerateAccountPayable"
                      onClick={() => handlerPayGenerateAccountPayable(row)}
                    >
                      Quitar e Gerar Conta a Pagar
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
                arrow
                placement="bottomCenter"
              >
                <Check className={getIconClass(row)} />
              </Dropdown>
            </Tooltip>
          )}

          {!!saveLoading(row) && <Spin size="small" />}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        permissions={route.permissions}
        title={route.name}
        extra={
          <SpaceForButton>
            <Button onClick={modalReportAction.open} block>
              Relatório
            </Button>

            <Button
              type="primary"
              disabled={
                !items.filter((item) => item.checked && !item.disabled).length
              }
              onClick={modalComissionsAction.open}
              block
            >
              Quitar comissões selecionadas
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          extraFilters={
            <EmployeeCommissionsFilter
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <Divider />

        <InfiniteTable
          loading={isLoading}
          columns={columns}
          dataSource={comissions}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetching={isFetching}
          rowKey={(row) => getKey(row)}
          scroll={{ x: getTableScrollX(columns) }}
          rowSelection={{
            selectedRowKeys: itemsCheckedKeys(),
            getCheckboxProps: (row) => ({ disabled: getItem(row)?.disabled }),
            onChange: (keys) => {
              setItems((state) =>
                state.map((item) => {
                  if (keys.includes(getKey(item))) {
                    return {
                      ...item,
                      checked: true,
                      payDate: item.disabled
                        ? item.payDate
                        : moment().format('YYYY-MM-DD'),
                    };
                  }
                  return {
                    ...item,
                    checked: false,
                    payDate: item.disabled ? item.payDate : null,
                  };
                }),
              );
            },
          }}
        />
      </TabContentWrapper>

      <EmployeePayCommissionModal
        form={form}
        open={modalComissionOpen}
        onOk={submitCommission}
        onCancel={modalComissionAction.close}
        confirmLoading={employeeComissionCreate.isLoading}
      />

      <EmployeePayCommissionsModal
        form={form}
        open={modalComissionsOpen}
        onOk={submitCommissions}
        onCancel={modalComissionsAction.close}
        confirmLoading={employeeComissionCreate.isLoading}
      />

      <EmployeeCommissionReportModal
        open={modalReportOpen}
        onCancel={modalReportAction.close}
      />
    </>
  );
};

export default EmployeeCommissionsPage;
